<template>
  <!-- <Toast /> -->
  <ErrorModal />
  <Toast />

  <div class="card-container title">

    <div class="flex">
      <h2 class="header">{{ storename }}</h2>
    </div>

  </div>
  <div class="container criteria">
    <div class="flex group">
      <div class="flex flex-0 firstgroup">
        <div class="flex flex-column generate dropdown">
          <label for="selectedReport">Report to Generate</label>
          <Dropdown :disabled="loading" v-model="selectedReport" :options="reports" optionLabel="label" optionGroupLabel="label" optionGroupChildren="items" placeholder="Select a Report" class="w-full md:w-14rem" @change="updateButtonReport">
            <template #optiongroup="slotProps">
                <div class="flex align-items-center">
                    <div><span style="width: 0.5rem;"></span>{{ slotProps.option.label }}</div>
                </div>
            </template>
          </Dropdown>
        </div>
        <div class="flex flex-column filter dropdown">
          <label for="selectedFields" v-if="fields.length > 0">Filter Columns</label>
          <MultiSelect v-if="fields.length > 0" v-model="selectedFields" :options="fields" :maxSelectedLabels="3" optionLabel="fieldname" placeholder="Select Columns" :showToggleAll="false" />
        </div>
      </div>
      <component :is="selectedReport.component">
        <div class="flex align-items-end submit">
          <Button v-if="getReportButton" :label="initialLoad ? 'Submit' : 'Update'" class="p-button p-component p-button-rounded btn-primary sml" style="height:35px;" @click="getReport" />
        </div>
      </component>
    </div>
  </div>

  <div class="container">
    <div class="card">
      <h3>{{ reportTitle }}</h3>
      <div class="flex-grow-1 table">
          <DataTable responsiveLayout="stack" :value="reportData" :paginator="true" :rows="10" stripedRows removableSort
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              :rowsPerPageOptions="[10, 20, 50]" breakpoint="800px"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords}" selectionMode="single" :loading="loading" dataKey="ServiceCategory">
              <template #loading>
                  <span>Loading Reports <i class="pi pi-spin pi-spinner"></i></span>
              </template>
              <template #empty>
                <span v-if="initialLoad">Select a Report</span>
                <span v-else>No Reports Found</span>      
              </template>
              <Column v-for="column in dynamicColumns" :key="column.fieldid" :field="column.fieldid" :header="column.fieldname" :sortable="true">
                <template v-if="column.format === 'currency'" #body="slotProps">
                  {{ formatCurrency(slotProps.data[column.fieldid]) }}
                </template>
                <template v-else-if="column.format === 'percentage'" #body="slotProps">
                  {{ formatPercentage(slotProps.data[column.fieldid]) }}
                </template>
                <!-- string -->
                <template v-else-if="column.format === undefined" #body="slotProps">
                  {{ formatUndefinedValue(slotProps.data[column.fieldid]) }}
                </template>
                <template v-if="overallTotal" #footer>
                  {{ getTotalData(column) }}
                </template>
              </Column>
          </DataTable>
          <div v-if="empty" class="container flex-grow-1">No reports found.</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import WorkInProgressSummary from './ReportItems/WorkInProgressSummary.vue';
  import VitalSignsByServiceCategory from './ReportItems/VitalSignsByServiceCategory.vue';
  import InvoiceSummary from './ReportItems/InvoiceSummary.vue';
  import ServicePackageSales from './ReportItems/ServicePackageSales.vue';
  import TechnicianSummary from './ReportItems/TechnicianSummary.vue';
  import { ref, onMounted, computed } from "vue";
  import { useAuth0 } from "@auth0/auth0-vue";
  import { openErrorModal } from '@/composables/useErrorLog';
  import ErrorModal from './ErrorModal.vue';
  import moment from 'moment';
  import DataTable from "primevue/datatable";
  import { getVitalSignsByServiceCategory, getInvoiceSummary, getServicePackageSales, getTechnicianSummary, getReportButton, updateButtonReport } 
    from '@/composables/useInputParameters';
  import { useStore } from 'vuex';

  const store = useStore();
  const empty = ref(false);
  const loading = ref(false);
  const startDate = ref(moment().startOf('month').format('MM/DD/YYYY'));
  const selectedFields = ref();
  const fields = ref([]);
  const overallTotal = ref();
  const endDate = ref(moment().endOf('month').format('MM/DD/YYYY'));
  const reports = ref([
    {
      label: 'Data Sets',
      items: [
          { label: '  Vital Signs by Service Category', value: 'VitalSignsByServiceCategory', component: VitalSignsByServiceCategory, input: getVitalSignsByServiceCategory },
          { label: '  Work In Progress Summary', value: 'PWAWorkInProgressSummary', component: WorkInProgressSummary, input: (() => { return '' }) },
          { label: '  Invoice Summary', value: 'PWAInvoiceSummary', component: InvoiceSummary, input: getInvoiceSummary },
          { label: '  Service Package Sales', value: 'PWAServicePackageSales', component: ServicePackageSales, input: getServicePackageSales },
          { label: '  Technician Summary', value: 'PWATechnicianSummary', component: TechnicianSummary, input: getTechnicianSummary },
      ]
    }
  ]);

  const selectedReport = ref('');
  const reportData = ref([]);
  const auth0 = useAuth0();
  const dynamicColumns = computed(() => {
    return Array.isArray(selectedFields.value)
      ? [...selectedFields.value].sort((a, b) => a.rank - b.rank)
      : [];
  });
  const initialLoad = ref(true);
  const reportTitle = ref('');
  const storename = ref();

  onMounted(async () => {
    storename.value = store.state.storename;
  });

  const getReport = async () => {
    if (!startDate.value || !endDate.value || !selectedReport.value) return;
    try {
      reportTitle.value = 'Loading Report';
      selectedFields.value = [];
      reportData.value = [];
      fields.value = [];
      loading.value = true;
      const storeid = store.state.storeid;
      const queryParameters = selectedReport.value.input();
      const url = `${process.env.VUE_APP_NODEJS_BASE_URL}reporting/${storeid}/${selectedReport.value.value}?${queryParameters}`;
      const accessToken = await auth0.getAccessTokenSilently();
      const response: Response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Accept-Encoding': 'gzip'
          },
      });

      const data = await response.json();

      if (response.status > 400) {
          throw new Error(JSON.stringify(data.error));
      }

      if(data.message.length === 0) {
        empty.value = true;
        reportData.value = [];
        return;
      }

      reportData.value = data.message.reportData;
      fields.value = data.message.fields;
      overallTotal.value = undefined;
      const index = reportData.value.findIndex(item => item.ServiceCategory === "Total");
      if (index !== -1) {
        overallTotal.value = reportData.value.splice(index, 1)[0];
      } else {
        overallTotal.value = getOverAllTotal(fields.value, reportData.value);
      }
      selectedFields.value = [...fields.value];
      empty.value = false;
      loading.value = false;
      getReportButton.value = false;
      initialLoad.value = false;
      reportTitle.value = selectedReport.value.label;
    } catch (e) {
        openErrorModal(e.message);
    }
  };

  const getTotalData = (field) => {
    const value = overallTotal.value[field.fieldid];
    if (field.format === 'currency') {
      return formatCurrency(value);
    } else if (field.format === 'percentage') {
      return formatPercentage(value);
    } else if (field.format === 'decimal') { // for labor hours, since we cannot determine if it is currency or percentage
      return `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    } else if (field.format === 'integer') {
      return value;
    }

    return value;
  };

  const formatCurrency = (value) => {
      if (value != '') {
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      }
      return value;
  };

  const formatPercentage = (value) => {
      if (value != '') {
        const transformedValue = value * 100;
        return `${transformedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
      }
      return value;
  };

  const formatUndefinedValue = (value) => {
      if (value != '') {
        return `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      return value;
  };

  const getOverAllTotal = (fields, reportData) => {
    const summationData = {};
    fields.forEach(field => { summationData[field.fieldid] = field.summation; });
    const totals = reportData.reduce((acc, curr) => 
      {
        for (let index = 0; index < fields.length; index++) {
          if (fields[index].summation === 0) {
            acc[fields[index].fieldid] += curr[fields[index].fieldid];
          } else {
            acc[fields[index].fieldid] = fields[index].summation; // get default summation if field cannot be summed
          }
        }
        return acc;
      },
      { ...summationData } // default data if no reportData
    );

    fields.forEach(field => {
      const fieldid = field.fieldid;
      if (field.summation === 0 && field.format === 'percentage') {
        totals[fieldid] = reportData.length > 0 ? totals[fieldid] / reportData.length : 0;
      }
    });
    return totals;
  }
</script>

<style>
  .card {
    /* background: #ffffff;
    padding: 1.5rem;
    margin-bottom: 1rem;
    border: 1px solid #EEEEEE; */
    border-radius: 25px;
  }
  .p-datatable .p-datatable-tfoot > tr > td {
    border: 1px solid #f0f3f7 !important;
    border-width: 1px 0 1px 0 !important;
    background: white !important;
  }
</style>
